import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { DataSourceTable } from '@components/new-table/data-sources/data-source-table';
import { RentalReservationService } from '@services/data-source-tables/rentals/rental-reservation.service';
import { RentalReservationTableSettings } from '@/table-settings/rentals/rental-reservation-table-settings';
import { DialogPopUpComponent } from '@components/dialog-pop-up/dialog-pop-up.component';
import { LoggerService } from '@services/logger.service';
import { RentalReservationModel } from '@/models/rentals/rental-reservation.model';
import { DialogFormRentalReservationComponent } from '@pages/rental-management/rental-reservation-list/dialog-form-rental-reservation/dialog-form-rental-reservation.component';

@Component({
  selector: 'app-rental-reservation-list',
  templateUrl: './rental-reservation-list.component.html',
  styleUrls: ['./rental-reservation-list.component.scss'],
})
export class RentalReservationListComponent implements OnInit, OnDestroy {
  displayedColumns = RentalReservationTableSettings.getColumnsSetting();
  dataSource = new DataSourceTable(this.rentalReservationService);
  reloadTrigger$ = new Subject<any>();

  private subscriptions: Subscription[] = [];

  constructor(
    private rentalReservationService: RentalReservationService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.handlePendingEditFromNavigation();
  }

  private handlePendingEditFromNavigation(): void {
    const navState =
      this.router.getCurrentNavigation()?.extras?.state ?? (history.state as Record<string, any>);
    const editId = navState?.['editReservationId'];
    if (editId == null) return;

    const editSub = this.dataSource.getElements$
      .pipe(
        filter((arr: any[]) => Array.isArray(arr) && arr.length > 0),
        take(1),
      )
      .subscribe((elements: any[]) => {
        const target = elements.find(
          (r: RentalReservationModel) => String(r.id) === String(editId),
        );
        if (target) {
          this.openReservationDialog(target, false);
        } else {
          this.toastr.info(
            `Prenotazione #${editId} non trovata nella pagina corrente. Cercala in lista.`,
          );
        }
        this.clearEditReservationIdFromHistory();
      });
    this.subscriptions.push(editSub);
  }

  private clearEditReservationIdFromHistory(): void {
    if (history.state?.editReservationId != null) {
      const cleaned = { ...history.state };
      delete cleaned.editReservationId;
      history.replaceState(cleaned, '', location.href);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  btnDetailsClickFunction(element: RentalReservationModel) {
    this.openReservationDialog(element, false);
  }

  openReservationDialog(reservation: RentalReservationModel | null, isNew: boolean) {
    this.dialog.open(DialogFormRentalReservationComponent, {
      data: {
        reservation: reservation || new RentalReservationModel(),
        isNew,
        reloadTrigger$: this.reloadTrigger$,
      },
      width: 'auto',
      panelClass: 'custom-modalbox',
    });
  }

  btnDeleteFunction({ id }: RentalReservationModel) {
    let dialogRef = this.dialog.open(DialogPopUpComponent, {
      width: 'auto',
      panelClass: 'custom-modalbox',
    });

    this.setupPopup(dialogRef);

    dialogRef.afterClosed().subscribe((deleteReservation) => {
      if (deleteReservation) {
        this.rentalReservationService.deleteReservation(id).subscribe({
          next: () => {
            this.reloadTrigger$.next(1);
            this.toastr.success('Prenotazione cancellata correttamente!');
          },
          error: (error) => {
            this.toastr.error('Errore durante la cancellazione della prenotazione.');

            LoggerService.error('deleteReservation failed!');
            LoggerService.error(error);
          },
          complete: () => {
            LoggerService.log('Request done');
          },
        });
      }
    });
  }

  private setupPopup(dialogRef: MatDialogRef<DialogPopUpComponent>) {
    dialogRef.componentInstance.title = 'Confermi di voler cancellare questa prenotazione?';
    dialogRef.componentInstance.content =
      'Attenzione una volta cancellata la prenotazione non sarà più possibile recuperarla.';
    dialogRef.componentInstance.firstCTA = 'Si cancella';
    dialogRef.componentInstance.secondCTA = 'Annulla operazione';
    dialogRef.componentInstance.closeResultFirst = true;
    dialogRef.componentInstance.closeResultSecond = false;
  }
}
